import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaShieldAlt, FaSearch, FaPhone, FaMapMarkerAlt, FaPlus
} from 'react-icons/fa';
import './SheriffOffices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SheriffOffices = () => {
  const [offices, setOffices] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchOffices();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOffices();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/sheriff-offices/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchOffices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);

      const response = await fetch(`${API_URL}/sheriff-offices?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setOffices(data.data);
        setError('');
      } else {
        setError('Failed to load sheriff offices');
      }
    } catch (err) {
      console.error('Error fetching offices:', err);
      setError('Failed to load sheriff offices');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: ''
    });
  };

  const handleOfficeClick = (officeId) => {
    navigate(`/address-book/sheriff-offices/${officeId}`);
  };

  if (loading && offices.length === 0) {
    return (
      <div className="sheriff-offices-container">
        <div className="loading">Loading sheriff's offices...</div>
      </div>
    );
  }

  return (
    <div className="sheriff-offices-container">
      {/* Header */}
      <div className="sheriff-offices-header">
        <div className="header-left">
          <FaShieldAlt size={32} />
          <div>
            <h1>Montana Sheriff's Offices</h1>
            <p className="subtitle">{offices.length} sheriff's offices found</p>
          </div>
        </div>
        <button className="btn-add-office" onClick={() => navigate('/address-book/sheriff-offices/new')}>
          <FaPlus /> Add Office
        </button>
      </div>

      {/* Filters */}
      <div className="sheriff-offices-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by county or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Offices List */}
      <div className="offices-list">
        {offices.length === 0 ? (
          <div className="no-results">
            <FaShieldAlt size={48} />
            <p>No sheriff's offices found matching your criteria</p>
          </div>
        ) : (
          offices.map(office => (
            <div
              key={office.id}
              className="office-card"
              onClick={() => handleOfficeClick(office.id)}
            >
              <div className="office-header">
                <div className="office-title">
                  <FaShieldAlt className="office-icon" />
                  <h3>{office.name}</h3>
                </div>
                <span className="county-badge">{office.county} County</span>
              </div>

              <div className="office-info">
                <div className="info-row">
                  {(office.address_city || office.city) && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>{office.address_city || office.city}, MT</span>
                    </div>
                  )}

                  {office.phone && (
                    <div className="info-item">
                      <FaPhone className="info-icon" />
                      <span>{office.phone}</span>
                    </div>
                  )}
                </div>

                {office.address_street && (
                  <div className="address-detail">
                    {office.address_street}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SheriffOffices;
